import React, { useState } from 'react';
import { CSVLink } from 'react-csv';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { EmployeeType } from '@prisma/client';
import { Button, Empty, FormInstance, FormItemProps, Tag, Tour, TourStepProps } from 'antd';
import _ from 'lodash';

import { BulkSubscriptionEligibiltyResult } from '@app/domain-bundle-interfaces';
import { SelectTable } from '@app/web-components';

import { useEligibilityTag } from '../../../../bundles';
import { EmployeeTypeRenderer } from '../../../../common';
import { EligibilityPopover } from '../../../../subscriptions/modals';

export const employeeTypeOptions = [
  { text: EmployeeType.INTEL, value: EmployeeType.INTEL },
  { text: EmployeeType.CONTINGENT_WORKER, value: EmployeeType.CONTINGENT_WORKER },
];

export const SelectUserAccessTooltipText = () => (
  <>
    <p>
      Once the file is uploaded and processed, you will be given the ability to <strong>Select User Accesses</strong>{' '}
      that you intend to subscribe to.
    </p>
    <p>
      According to the worker in the file and the access selected, the application will enable or disable the user
      access items based on validations. The result of the validations will be displayed in the Eligibility column. To
      view detailed validation results, hover over the eligibility result tag for that line item. You have the option to
      download the enterprise IDs of ineligible workers in the same format as your upload. To do this, click the{' '}
      <strong>Export Ineligible Workers</strong> button, which is located at the bottom left of the{' '}
      <strong>Select User Access</strong> table.
    </p>
    <p>
      Since the validations are related to DMS, product family, DIE, process, etc, the best channel to ensure if they
      really should have access to this specific item is to revisit with the CW BB Sponsor or Manager.
    </p>
    <p>
      For more information on the validations: Blue Badge users (Intel employees)&nbsp;
      <a href="https://wiki.ith.intel.com/pages/viewpage.action?pageId=3040657977" target="_blank" rel="noreferrer">
        click here
      </a>
      &nbsp; and Green Badge users (Contingent Workers)&nbsp;
      <a href="https://wiki.ith.intel.com/pages/viewpage.action?pageId=3485306786" target="_blank" rel="noreferrer">
        click here
      </a>
      .
    </p>
    <p>At least one user access must be selected.</p>
  </>
);

export const useSelectUserAccessFormItem = (
  bulkUploadTableData: BulkSubscriptionEligibiltyResult[],
  form: FormInstance,
  bundleName: string,
) => {
  const { getProps: getTagProps } = useEligibilityTag();
  const [open, setOpen] = useState(false);
  const tagRef = React.useRef<HTMLElement>(null);

  const steps: TourStepProps[] = [
    {
      title: 'Select User Accesses',
      description: (
        <>
          <p>
            Users select items from the file processing results to subscribe to. Subscribing to an item does not
            guarantee access. SAAF will request each item selected from the system of origin on your behalf.
          </p>
          <p>At least one item must be selected.</p>
        </>
      ),
    },
    {
      title: 'Eligibility',
      description: (
        <>
          <p>
            Based on the worker in the file and bundle access, the application will determine the eligibility of user
            items through validations. These validations are represented by tags that indicate the eligibility of the
            worker for the item.{' '}
            <strong>
              For a more detailed view of the validation results, you can hover over or click on the eligibility result
              tag.
            </strong>
          </p>
          <p>
            If a worker is not eligible for an access item, then any related dependent access items will also be
            disabled.
          </p>
          <p>
            Users have the option to select items individually or all at once using the dropdown arrow next to the top
            selection box. By choosing <strong>Select-All-Eligible</strong>, all available items will be selected.
            Alternatively, any current selections can be cleared by clicking on <strong>Clear-Selection</strong>. Please
            be aware that only the <strong>Clear-Selection</strong> option will remove all selected items across all
            pages. Unchecking the top selection box will not achieve this.
          </p>
          <p>
            You have the option to download the enterprise IDs of ineligible workers in the same format as your upload.
            To do this, click the <strong>Export Ineligible Workers</strong> button, which is located at the bottom left
            of the <strong>Select User Access</strong> table.
          </p>
          <p>
            Since the validations are related to DMS, product family, DIE, process, etc, the best channel to ensure if
            they really should have access to this specific item is to revisit with the CW BB Sponsor or Manager.
          </p>
          <Button
            type="link"
            style={{ paddingLeft: 0 }}
            onClick={() => window.open('https://wiki.ith.intel.com/pages/viewpage.action?pageId=3040657977', '_blank')}
          >
            Click for more information on Intel Worker validations.
          </Button>
          <Button
            type="link"
            style={{ paddingLeft: 0 }}
            onClick={() => window.open('https://wiki.ith.intel.com/pages/viewpage.action?pageId=3485306786', '_blank')}
          >
            Click for more information on Contingent Worker validations.
          </Button>
        </>
      ),
      target: () => tagRef.current as HTMLElement,
    },
  ];
  const getProps = (): FormItemProps => ({
    children: (
      <SelectTable
        onChange={(value) => {
          console.log('onChange', value);
        }}
        columns={[
          {
            title: 'Enterprise Id',
            dataIndex: 'enterpriseId',
            sorter: (a, b) => a.enterpriseId.localeCompare(b.enterpriseId),
            width: 120,
          },
          {
            title: 'User Name',
            dataIndex: 'userName',
            sorter: (a, b) => a.userName.localeCompare(b.userName),
            width: 120,
          },
          {
            title: 'Employee Type',
            dataIndex: 'employeeType',
            width: 120,
            filters: employeeTypeOptions,
            onFilter: (value, record) => record.employeeType === value,
            sorter: (a, b) => a.employeeType.localeCompare(b.employeeType),
            render: (_, record) => <EmployeeTypeRenderer employeeType={record.employeeType as EmployeeType} />,
          },
          {
            title: 'Is Eligible',
            dataIndex: 'isEligible',
            width: 120,
            render: (isEligible, record, index) =>
              record.messages &&
              typeof record.isEligible === 'boolean' && (
                <EligibilityPopover messages={record.messages}>
                  <Tag ref={index === 0 ? tagRef : undefined} {...getTagProps(isEligible)} />
                </EligibilityPopover>
              ),
            filters: [
              { text: 'True', value: true },
              { text: 'False', value: false },
            ],
            onFilter: (value, record) => record.isEligible === value,
            sorter: (a, b) => a.isEligible.toString().localeCompare(b.isEligible.toString()),
            filterMultiple: false,
          },
          {
            title: 'Access',
            dataIndex: 'accessName',
            width: 120,
            filters: _.uniqBy(
              bulkUploadTableData.map((x) => ({ text: x.accessName, value: x.accessName })),
              'value',
            ),
            sorter: (a, b) => a.accessName.localeCompare(b.accessName),
            onFilter: (value, record) => record.accessName === value,
            filterMultiple: false,
          },
        ]}
        dataSource={bulkUploadTableData}
        footer={() => (
          <CSVLink
            data={bulkUploadTableData.filter((x) => !x.isEligible).map(({ enterpriseId }) => [enterpriseId])}
            headers={[]}
            enclosingCharacter=""
            filename={`${bundleName}-ineligible-workers.txt`}
          >
            Export Ineligible Workers
          </CSVLink>
        )}
        rowKey={(record) => `${record.userId}-${record.accessId}`}
        rowSelection={{
          getCheckboxProps: (record: BulkSubscriptionEligibiltyResult) => ({
            disabled: !record.isEligible, // Column configuration not to be checked
          }),
          selections: [
            {
              key: 'all',
              text: 'Select-All-Eligible',
              onSelect: () => {
                const selectedRowKeys = bulkUploadTableData
                  .filter((x) => x.isEligible)
                  .map((x) => `${x.userId}-${x.accessId}`);
                form.setFieldsValue({ userAccesses: selectedRowKeys });
              },
            },
            {
              key: 'none',
              text: 'Clear-Selection',
              onSelect: () => {
                form.setFieldsValue({ userAccesses: [] });
              },
            },
          ],
        }}
        renderEmpty={
          bulkUploadTableData.length
            ? undefined
            : () => (
                <Empty
                  description="Select a bundle and upload file to view user accesses."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )
        }
      />
    ),
    label: 'Select User Accesses',
    name: 'userAccesses',
    rules: [{ required: true, message: 'Must select at least one eligible user access.' }],
    tooltip: {
      icon: (
        <span>
          <QuestionCircleOutlined onClick={() => setOpen(true)} />
          <Tour open={open} steps={steps} onClose={() => setOpen(false)} onFinish={() => setOpen(false)} />
        </span>
      ),
      title: 'Click for info on selecting user-access and eligibilty',
    },
  });

  return { getProps };
};
